import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, InputObservable } from '@y42/angular/util';
import { ConfirmDialogComponent } from '@y42/core/dialog/confirm-dialog';
import { ErrorHandlerService } from '@y42/error-handling';
import { FilePath, GitCommit, GitDiff, GitQuery, GitService } from '@y42/git';
import { OrgUserQuery } from '@y42/org-user/state';
import { ToastService } from '@y42/toast';
import { BehaviorSubject, filter, Observable, of, switchMap, tap } from 'rxjs';
import { GitCommitHelper } from '../../git-commit.helper';
import { GitPathMapper } from '../../git-path-mapper';

@UntilDestroy()
@Component({
  selector: 'y42-git-commit-details',
  templateUrl: './git-commit-details.component.html',
  styleUrls: ['./git-commit-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [GitCommitHelper],
})
export class GitCommitDetailsComponent implements OnInit {
  constructor(
    private readonly gitQuery: GitQuery,
    private readonly gitService: GitService,
    private readonly dialog: MatDialog,
    private readonly orgUserQuery: OrgUserQuery,
    private readonly gitCommitHelper: GitCommitHelper,
    private readonly gitPathMapper: GitPathMapper,
    private readonly toast: ToastService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  @Input()
  @InputObservable()
  commit!: GitCommit;
  commit$!: Observable<GitCommit | undefined>;

  @Output() checkedOut = new EventEmitter<void>();

  showBlame$ = new BehaviorSubject(false);

  readonly state = connectState(this, {
    diffs: this.commit$.pipe(
      tap(() => {
        this.getDiffLoading = true;
      }),
      switchMap((commit) =>
        // since diff size is potentially big we load it on only commit selection
        commit ? this.gitService.getDiff(this.commit.parentHash, this.commit.hash, this.filePath) : of(null),
      ),
      tap((diffs) => {
        this.showBlame$.next(false);
        this.getDiffLoading = false;
        if (this.filePath && diffs) {
          this.activeDiff = diffs.find((item) => item.path === this.filePath) || null;
        } else {
          this.activeDiff = diffs?.[0] ?? null;
        }
      }),
    ),
    user: this.commit$.pipe(
      switchMap((commit) => (commit ? this.orgUserQuery.selectUIEntity(this.commit.authorEmail) : of(null))),
    ),
    showBlame: this.showBlame$,
    hasChangedFiles: this.gitQuery.hasModifiedFiles$,
  });

  @Input() filePath!: FilePath | undefined;

  @Input() loading = false;

  activeDiff: GitDiff | null = null;

  getDiffLoading = true;

  showDiffList = false;

  isDir = false;

  canRevert = true;

  ngOnInit() {
    this.isDir = !!this.filePath && this.gitQuery.isDir(this.filePath);
    this.showDiffList = !this.filePath || this.isDir;
    if (this.filePath) {
      const pathInfo = this.gitPathMapper.getPathInfo(this.filePath);
      this.canRevert = !!pathInfo?.canRevert;
    }
  }

  async revert(filePath: string | undefined = this.filePath) {
    if (this.gitCommitHelper.checkForUncommittedChanges()) {
      return;
    }

    try {
      await this.gitService.revert(this.commit, filePath ? [filePath] : undefined);
    } catch (error: unknown) {
      this.errorHandlerService.handleError(error);
    }
  }

  onMonacoEditorInit(editor: monaco.editor.IStandaloneDiffEditor) {
    editor.addAction({
      id: 'git-blame',
      label: 'Annotate with Git Blame',
      contextMenuGroupId: 'navigation',
      run: () => this.showBlame$.next(true),
    });
  }

  async tryToCheckout(hash: string) {
    const activeBranchName = this.gitQuery.getActiveBranchName();
    const branchHead = (await this.gitService.readFile(`.git/refs/heads/${activeBranchName}`)).trim();
    if (branchHead === hash) {
      this.toast.warning('You are already on that commit');
      return;
    }

    this.dialog
      .open(ConfirmDialogComponent, {
        width: '600px',
        data: {
          data: 1,
          text: `Are you sure you would like to checkout this hash?<br/> It will create a separated branch from that commit.`,
          confirmation_text: 'Checkout',
          header_text: `Checkout ${hash.slice(0, 8)}`,
        },
        panelClass: ['confirmation-pop-up'],
      })
      .afterClosed()
      .pipe(
        filter(Boolean),
        switchMap(() => this.checkoutHash(hash)),
      )
      .subscribe();
  }

  private async checkoutHash(hash: string) {
    if (this.state.hasChangedFiles) {
      this.toast.warning(
        'You have uncommitted changes. Please commit them or discard these changes before checking out this hash.',
      );
      return;
    }

    this.checkedOut.emit();

    const branchName = `detached-${hash}`;
    await this.gitService.createBranch(branchName, { use: 'active' }, hash);
  }
}
